<template>
  <v-dialog v-model="dialogVisible" max-width="800px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ isCreate ? "Create Task" : "Edit Task" }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container>
            <v-row>
              <!-- Display Name -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedTask.displayName"
                  label="Display Name"
                  :rules="[v => !!v || 'Display name is required']"
                  required
                ></v-text-field>
              </v-col>

              <!-- Code (display only) -->
              <v-col cols="12" md="6" v-if="!isCreate && editedTask.code">
                <v-label class="text-caption text--secondary mb-2">Code</v-label>
                <div>
                  <v-chip small color="primary" outlined>
                    {{ editedTask.code }}
                  </v-chip>
                </div>
              </v-col>

              <!-- Description -->
              <v-col cols="12">
                <v-textarea
                  v-model="editedTask.description"
                  label="Description"
                  rows="3"
                  :rules="[v => !!v || 'Description is required']"
                  required
                ></v-textarea>
              </v-col>

              <!-- Periodicity -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedTask.periodicity"
                  :items="staticData.taskPeriodicities || []"
                  item-text="text"
                  item-value="value"
                  label="Periodicity"
                  :rules="[v => !!v || 'Periodicity is required']"
                  required
                ></v-select>
              </v-col>

              <!-- Active Status -->
              <v-col cols="12" md="6">
                <v-switch v-model="editedTask.isActive" label="Active" color="success"></v-switch>
              </v-col>

              <!-- Email Notification -->
              <v-col cols="12" md="6">
                <v-switch
                  v-model="editedTask.needToSendEmail"
                  label="Send Email Notification"
                  color="info"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Rewards Section -->
            <v-row>
              <v-col cols="12">
                <v-subheader class="pl-0">
                  <span class="text-h6">Rewards</span>
                  <v-spacer></v-spacer>
                  <v-btn small color="primary" @click="addReward">
                    <v-icon left small>mdi-plus</v-icon>
                    Add Reward
                  </v-btn>
                </v-subheader>
              </v-col>
            </v-row>

            <!-- Rewards List -->
            <v-row v-if="editedTask.rewards && editedTask.rewards.length > 0">
              <v-col cols="12">
                <v-card outlined>
                  <v-list>
                    <v-list-item
                      v-for="(reward, index) in editedTask.rewards"
                      :key="reward.id || index"
                      class="px-4"
                    >
                      <v-list-item-content>
                        <v-row align="center">
                          <v-col cols="12" md="5">
                            <v-select
                              v-model="reward.rewardType"
                              :items="mappedRewardTypes"
                              item-text="text"
                              item-value="value"
                              label="Reward Type"
                              dense
                              :rules="[v => !!v || 'Reward type is required']"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="5">
                            <v-text-field
                              v-model.number="reward.value"
                              label="Value"
                              type="number"
                              dense
                              :rules="[v => v > 0 || 'Value must be greater than 0']"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-btn icon color="error" @click="removeReward(index)">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>

            <!-- No Rewards Message -->
            <v-row v-else>
              <v-col cols="12">
                <v-alert type="info" outlined>
                  No rewards configured. Click "Add Reward" to add one.
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!valid" @click="saveTask">
          {{ isCreate ? "Create" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "TaskEditModal",
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      default: null
    },
    staticData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      editedTask: {
        id: null,
        displayName: "",
        description: "",
        periodicity: "",
        isActive: true,
        needToSendEmail: false,
        rewards: []
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value) {
          this.closeDialog();
        }
      }
    },
    isCreate() {
      return !this.task || !this.task.id;
    },
    mappedRewardTypes() {
      const rewardTypeMapping = {
        OOBLEE_YELLOW: "Ooblee Yellow",
        OOBYTE: "Oobyte",
        CASH_WITHDRAWBAL: "Withdrawable cash",
        CASH_NON_WITHDRAWBAL: "Non Withdrawable cash"
      };

      if (!this.staticData.taskRewardTypes) return [];

      return this.staticData.taskRewardTypes.map(type => ({
        text: rewardTypeMapping[type] || type,
        value: type
      }));
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.initializeTask();
      }
    },
    task: {
      handler() {
        if (this.dialog) {
          this.initializeTask();
        }
      },
      deep: true
    }
  },
  methods: {
    initializeTask() {
      if (this.task) {
        this.editedTask = {
          ...this.task,
          rewards: this.task.rewards ? [...this.task.rewards] : []
        };
      } else {
        this.editedTask = {
          id: null,
          displayName: "",
          description: "",
          periodicity: "",
          isActive: true,
          needToSendEmail: false,
          rewards: []
        };
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    addReward() {
      this.editedTask.rewards.push({
        id: null,
        taskId: this.editedTask.id,
        rewardType: "",
        value: 0
      });
    },
    removeReward(index) {
      this.editedTask.rewards.splice(index, 1);
    },
    async saveTask() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.saving = true;
      try {
        // Create a clean copy without createdAt and code fields
        const taskData = { ...this.editedTask };
        delete taskData.createdAt;
        delete taskData.code;

        // Clean rewards array - keep only rewardType and value
        if (taskData.rewards && taskData.rewards.length > 0) {
          taskData.rewards = taskData.rewards.map(reward => ({
            rewardType: reward.rewardType,
            value: reward.value
          }));
        }

        this.$emit("save", taskData);
      } catch (error) {
        console.error("Error saving task:", error);
      } finally {
        this.saving = false;
      }
    },
    closeDialog() {
      this.$emit("close");
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.reset();
          this.$refs.form.resetValidation();
        }
      });
    }
  }
};
</script>

<style scoped>
.v-subheader {
  height: auto;
  padding: 16px 0;
}
</style>
